import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get } from '../../lib/apiClient';
import { cn } from '@vd/ui';
import { AdminTable, type AdminTableColumn } from './AdminTable';
import { planClass } from './planBadge';
import styles from './Admin.module.css';
import { formatWhen } from '../../lib/time';

interface Cancellation {
  id: string;
  companyId: string;
  companyName: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  source: 'in_app' | 'stripe_portal';
  reason: string | null;
  feedback: string | null;
  planAtCancel: string;
  mrrAtCancel: number | null;
  occurredAt: string | null;
}

interface CancellationsResponse {
  cancellations: Cancellation[];
  total: number;
}

type SourceFilter = 'all' | 'in_app' | 'stripe_portal';

export function CancellationsPage() {
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'cancellations'],
    queryFn: () => get<CancellationsResponse>('/admin/cancellations'),
    staleTime: 30_000,
  });
  const navigate = useNavigate();

  const filtered = (data?.cancellations ?? []).filter((c) => sourceFilter === 'all' || c.source === sourceFilter);
  const totalMrrLostCents = filtered.reduce((acc, c) => acc + (c.mrrAtCancel ?? 0), 0);

  const columns: AdminTableColumn<Cancellation>[] = [
    {
      key: 'when', label: 'When', sortable: true,
      sortAccessor: (r) => r.occurredAt ?? '',
      render: (c) => <span className={styles.muted}>{formatDateTime(c.occurredAt)}</span>,
    },
    {
      key: 'account', label: 'Account', sortable: true,
      sortAccessor: (r) => (r.companyName ?? r.companyId).toLowerCase(),
      render: (c) => (
        <div className={styles.userCell}>
          <button
            type="button"
            className={cn(styles.userName, styles.linkChip)}
            onClick={() => navigate(`/admin/accounts?focus=${encodeURIComponent(c.companyId)}`)}
            title="Open in Accounts"
          >
            {c.companyName ?? c.companyId}
          </button>
          <span className={styles.userEmail}>{c.companyId}</span>
        </div>
      ),
    },
    {
      key: 'by', label: 'By',
      render: (c) => c.userId
        ? (
          <button
            type="button"
            className={cn(styles.linkChip)}
            onClick={() => navigate(`/admin/users?focus=${encodeURIComponent(c.userId!)}`)}
            title="Open user"
          >
            <div className={styles.userCell}>
              <span style={{ fontSize: 12 }}>{c.userName ?? c.userId}</span>
              <span className={styles.userEmail}>{c.userEmail}</span>
            </div>
          </button>
        )
        : <span className={styles.muted}>-</span>,
    },
    {
      key: 'source', label: 'Source', sortable: true,
      sortAccessor: (r) => r.source,
      render: (c) => (
        <span className={c.source === 'in_app' ? styles.planPersonal : styles.planTeam} style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11 }}>
          {c.source === 'in_app' ? 'in-app' : 'portal'}
        </span>
      ),
    },
    {
      key: 'plan', label: 'Plan', sortable: true, sortAccessor: (r) => r.planAtCancel,
      render: (c) => <span className={cn(styles.planBadge, planClass(c.planAtCancel))}>{c.planAtCancel}</span>,
    },
    {
      key: 'mrr', label: 'MRR lost', sortable: true,
      sortAccessor: (r) => r.mrrAtCancel ?? -1,
      render: (c) => c.mrrAtCancel == null ? <span className={styles.muted}>-</span> : `$${(c.mrrAtCancel / 100).toFixed(0)}/mo`,
    },
    {
      key: 'feedback', label: 'Reason / Feedback',
      render: (c) => (
        <div style={{ maxWidth: 480 }}>
          {c.reason && <div style={{ fontWeight: 500, marginBottom: c.feedback ? 4 : 0 }}>{c.reason}</div>}
          {c.feedback && <div className={styles.muted} style={{ fontSize: 12, lineHeight: 1.4 }}>{c.feedback}</div>}
          {!c.reason && !c.feedback && <span className={styles.muted}>-</span>}
        </div>
      ),
    },
  ];

  return (
    <>
      <h1 className={styles.h1}>Cancellations</h1>
      <p className={styles.sub}>
        Captures from the in-app form (filled before the Stripe-portal redirect)
        and from Stripe's own cancellation flow (forwarded via webhook). Deduped
        within 24h so a user who fills the form AND completes in Stripe gets
        one row.
        {data ? ` ${filtered.length} total · ~$${(totalMrrLostCents / 100).toFixed(0)}/mo MRR represented.` : ''}
      </p>

      {error ? (
        <p style={{ color: 'var(--danger)' }}>
          Failed to load cancellations: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      ) : isLoading || !data ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <AdminTable<Cancellation>
          columns={columns}
          rows={filtered}
          getRowId={(c) => c.id}
          searchTextFor={(c) => `${c.companyName ?? ''} ${c.companyId} ${c.userName ?? ''} ${c.userEmail ?? ''} ${c.reason ?? ''} ${c.feedback ?? ''}`}
          searchPlaceholder="Search accounts, users, or feedback text…"
          initialSortKey="when"
          initialSortDir="desc"
          emptyText="No cancellations yet. They'll land here when an owner fills the in-app form OR cancels via Stripe's customer portal (with cancellation reasons + feedback enabled in Stripe → Customer Portal config)."
          filterBar={() => (
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Source
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
                style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'inherit' }}
              >
                <option value="all">All</option>
                <option value="in_app">In-app form</option>
                <option value="stripe_portal">Stripe portal</option>
              </select>
            </label>
          )}
        />
      )}
    </>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return formatWhen(d);
  } catch {
    return '-';
  }
}
